import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEntitlements } from '@/hooks/use-entitlements';
import { updateJobVisit, type PaymentMethod, type PaymentStatus } from '@/lib/api/jobs';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';
import { fillTemplate, formatMoney } from '@/lib/utils';

import {
  availablePaymentMethodOptions,
  formatDate,
  paymentMethodOptionValue,
  resolvePaymentMethodLabel,
} from './helpers';
import {
  PAYMENT_METHODS,
  recordPaymentSchema,
  type PaymentMethodValue,
  type RecordPaymentInput,
} from './schema';

interface Props {
  businessId: string;
  jobId: string;
  visitId: string;
  totalAmount: number | null;
  paymentStatus: PaymentStatus;
  amountCollected?: number | null;
  paymentMethod?: string | null;
  paymentMethodId?: string | null;
  paidAt?: string | null;
  completedAt?: string | null;
  onDone?: () => void;
}

// Reusable "Record payment" control. Mounted inline in the visit table and on
// the job detail page. The global MutationCache handles the refetch + success
// toast. Payment is captured against a single visit.
export function RecordPaymentForm({
  businessId,
  jobId,
  visitId,
  totalAmount,
  paymentStatus,
  amountCollected,
  paymentMethod,
  paymentMethodId,
  paidAt,
  completedAt,
  onDone,
}: Props) {
  const { t } = useLanguage();
  const { has } = useEntitlements();

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  const options = availablePaymentMethodOptions(
    t,
    paymentMethodsQuery.data ?? [],
    has('stripe_payments'),
  );

  // `paymentMethod` may be legacy free text pre-dating the enum; only trust
  // it as a real type when it's one of the known values.
  const knownType: PaymentMethod | null = PAYMENT_METHODS.includes(
    paymentMethod as PaymentMethodValue,
  )
    ? (paymentMethod as PaymentMethod)
    : null;

  // New visits are seeded with the job's default payment method (preferring
  // the specific account id, falling back to the first configured account of
  // the same type), or cash if nothing matches.
  const seededValue = paymentMethodOptionValue(options, knownType, paymentMethodId) || 'cash';

  const form = useForm<RecordPaymentInput>({
    defaultValues: {
      amountCollected: totalAmount != null ? String(totalAmount) : '',
      paymentMethod: seededValue,
    },
    resolver: zodResolver(recordPaymentSchema),
  });

  // The payment-methods list loads async; once it (or the entitlement gate)
  // resolves, re-seed the field so it lands on the right option instead of
  // the mount-time guess above — unless the user has already touched it.
  useEffect(() => {
    if (form.formState.dirtyFields.paymentMethod) return;
    const resolved = paymentMethodOptionValue(options, knownType, paymentMethodId) || 'cash';
    if (resolved !== form.getValues('paymentMethod')) {
      form.setValue('paymentMethod', resolved);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [paymentMethodsQuery.data, has('stripe_payments')]);

  const mutation = useMutation({
    mutationFn: (data: RecordPaymentInput) => {
      const selected = options.find((o) => o.value === data.paymentMethod);
      return updateJobVisit(businessId, jobId, visitId, {
        payment_status: 'paid',
        amount_collected: Number(data.amountCollected),
        payment_method: selected?.type ?? 'cash',
        payment_method_id: selected?.id ?? null,
      });
    },
    meta: { successMessage: t.jobPaymentRecorded },
    onSuccess: () => onDone?.(),
  });

  if (paymentStatus === 'paid') {
    const isPartial =
      amountCollected != null &&
      totalAmount != null &&
      amountCollected < totalAmount;
    const known = resolvePaymentMethodLabel(
      t,
      paymentMethodsQuery.data,
      knownType,
      paymentMethodId ?? null,
    );

    return (
      <div className="flex flex-col gap-1.5 text-sm" data-testid="payment-recorded">
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {amountCollected != null ? formatMoney(amountCollected) : '—'}
          </span>
          {known && <span className="text-muted-foreground">· {known}</span>}
          {isPartial && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {fillTemplate(t.seriesPartialShort, {
                amount: formatMoney((totalAmount ?? 0) - (amountCollected ?? 0)),
              })}
            </span>
          )}
        </div>
        {paidAt && (
          <span className="text-xs text-muted-foreground">
            {fillTemplate(t.jobPaidOn, { date: formatDate(paidAt.slice(0, 10)) })}
          </span>
        )}
        {completedAt && (
          <span className="text-xs text-muted-foreground">
            {fillTemplate(t.jobCompletedOn, {
              date: formatDate(completedAt.slice(0, 10)),
            })}
          </span>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
      className="flex flex-col gap-3"
      data-testid="record-payment-form"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Controller
          control={form.control}
          name="amountCollected"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="record-payment-amount">{t.jobAmountCollected}</Label>
              <Input
                {...field}
                id="record-payment-amount"
                type="number"
                min={0}
                step="0.01"
                className="w-32"
                aria-invalid={fieldState.invalid}
                data-testid="record-payment-amount"
              />
              {fieldState.error && (
                <p className="text-xs text-destructive">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
        <Controller
          control={form.control}
          name="paymentMethod"
          render={({ field }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="record-payment-method">{t.jobPaymentMethod}</Label>
              <select
                {...field}
                id="record-payment-method"
                data-testid="record-payment-method"
                className="px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        />
        <Button
          type="submit"
          size="sm"
          disabled={mutation.isPending}
          data-testid="record-payment-submit"
        >
          {t.jobPaymentSave}
        </Button>
      </div>
    </form>
  );
}
